/**
 * Derive Live Activity dashboard counters from the `getActiveSessions` payload.
 *
 * The raw payload conflates three different units, which made the header tiles
 * lie (ra-udhq.2): "Devices" summed device-rows per session instead of counting
 * distinct device UUIDs, "Active sessions" counted session rows but read like a
 * boat count, and stale-but-still-"active" sessions never flipped to finished so
 * the finished tile stayed at 0.
 *
 * Counts must dedupe at the right unit:
 *   - Boats   = distinct sail number (`vesselId`)
 *   - Devices = distinct device UUID (`deviceId`)
 *   - Sessions = session rows
 *
 * Route-mismatch and the auto/manual/inferred finished split are intentionally
 * NOT computed here — they depend on cross-referencing the day's scheduled races
 * (ra-udhq.5) and on a finish-reason that the `_meta` doc does not yet record
 * (abandoned sessions keep `status: active`; only a manual finish writes
 * `status: finished`). See meta_doc_writer.dart.
 */

/** A boat counts as "on the water" if it transmitted within this window. */
export const ON_WATER_WINDOW_MS = 30 * 60_000;
/** A boat counts as "transmitting now" if its last event is this recent. */
export const TRANSMITTING_WINDOW_MS = 2 * 60_000;
/** An `active`-status session older than this is a cleanup candidate. */
export const STALE_SESSION_MS = 30 * 60_000;

/**
 * Most recent device event time for a session, in epoch ms, or null if the
 * session has no devices / no events yet.
 */
export function sessionLastActivityMs(session) {
  if (!session?.devices?.length) return null;
  let latest = null;
  for (const d of session.devices) {
    if (!d.lastEventTime) continue;
    const t = new Date(d.lastEventTime).getTime();
    if (Number.isNaN(t)) continue;
    if (latest == null || t > latest) latest = t;
  }
  return latest;
}

/**
 * Compute the dashboard counters.
 *
 * @param {object} data  Payload from getActiveSessions: { active: [], finished: [] }.
 * @param {number} now   Reference time in epoch ms (injectable for tests).
 * @returns {{
 *   boatsOnWater: number,
 *   transmittingNow: number,
 *   activeSessions: number,
 *   devices: number,
 *   needsCleanup: number,
 *   finished24h: number,
 * }}
 */
export function deriveSessionStats(data, now = Date.now()) {
  const active = Array.isArray(data?.active) ? data.active : [];
  const finished = Array.isArray(data?.finished) ? data.finished : [];

  const onWater = new Set();
  const transmitting = new Set();
  const distinctDevices = new Set();
  let needsCleanup = 0;

  for (const s of active) {
    const lastMs = sessionLastActivityMs(s);
    const ageMs = lastMs == null ? null : now - lastMs;

    for (const d of s.devices || []) {
      if (d.deviceId) distinctDevices.add(d.deviceId);
    }

    const sailNo = s.vesselId || s.vessel;

    if (ageMs != null && ageMs <= ON_WATER_WINDOW_MS) {
      if (sailNo) onWater.add(sailNo);
    } else {
      // active status but no recent activity → likely abandoned, needs cleanup
      needsCleanup += 1;
    }

    if (ageMs != null && ageMs <= TRANSMITTING_WINDOW_MS && sailNo) {
      transmitting.add(sailNo);
    }
  }

  return {
    boatsOnWater: onWater.size,
    transmittingNow: transmitting.size,
    activeSessions: active.length,
    devices: distinctDevices.size,
    needsCleanup,
    finished24h: finished.length,
  };
}
